import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'redux/toolkit/hooks'

import { isFailed, isPending, isSuccess } from 'redux/toolkit/api'
import { AccountUser, AccountUserRole } from 'types/users'
import { DomainsInDB } from 'types/redux/user/UserTypes'
import {
  getAccountUsers,
  resetAccountUsers,
  resetUpdateAccountUserRole,
  updateAccountUserRole
} from 'redux/features/users/usersSlice'
import {
  IsAllCheckboxSelected,
  IsCheckboxIdIsSelected,
  OnSelectAllCheckboxes,
  OnSelectCheckbox,
  SelectedCheckboxes,
  useDatatableCheckboxColumnLogic
} from 'lib/useDatatableCheckboxColumnLogic'
import { setSuccessSnackBar } from 'redux/features/app/appSlice'

// V2 module imports
import { AuthSubject } from 'v2/modules/lib'

export enum EditUserDialogErrors {
  missedDomainSelection = 'missed_domain_selection',
  userUpdateError = 'user_update_error'
}

export type State = {
  selectedUserRole: AccountUserRole | ''
  domains?: DomainsInDB[]
  selectedDomains: SelectedCheckboxes
  isAllDomainsSelected: IsAllCheckboxSelected
  dialogError?: EditUserDialogErrors
  updateInProgress: boolean
  availableUserRoles: AccountUserRole[]
}

export type EventHandlers = {
  onSave: () => void
  onCancel: () => void
  onSelectUserRole: (newRole: AccountUserRole) => void
  onSelectDomain: OnSelectCheckbox
  onSelectAllDomains: OnSelectAllCheckboxes
  isDomainSelected: IsCheckboxIdIsSelected
}

export type UseEditUserDialogLogicProps = {
  onClose: () => void
  user: AccountUser
}
export type UseEditUserDialogLogic = [State, EventHandlers]

export const useEditUserDialogLogic = ({ onClose, user }: UseEditUserDialogLogicProps): UseEditUserDialogLogic => {
  const { domains, isUpdateUserRoleInProgress, isUpdateUserRoleSuccess, isUpdateUserRoleFailed, authDomainPolicies } =
    useAppSelector(_store => ({
      domains: _store.user.availableDomains,
      isUpdateUserRoleInProgress: isPending(_store.users.api.updateAccountUserRoleApiStatus),
      isUpdateUserRoleSuccess: isSuccess(_store.users.api.updateAccountUserRoleApiStatus),
      isUpdateUserRoleFailed: isFailed(_store.users.api.updateAccountUserRoleApiStatus),
      authDomainPolicies: _store.auth.accessTokenObject?.authDomainPolicies || []
    }))
  const dispatch = useAppDispatch()
  const [selectedUserRole, setSelectedUserRole] = useState<AccountUserRole | ''>(user.role)
  const [dialogError, setDialogError] = useState<EditUserDialogErrors | undefined>()
  const [
    selectedDomains,
    onSelectDomain,
    onSelectAllDomains,
    isDomainSelected,
    isAllDomainsSelected,
    getSelectedIds,
    resetSelectedDomains
  ] = useDatatableCheckboxColumnLogic()

  // init
  useEffect(
    () => () => {
      if (isUpdateUserRoleSuccess) {
        dispatch(resetUpdateAccountUserRole())
        dispatch(resetAccountUsers())
        dispatch(getAccountUsers())
      }
    },
    [dispatch, isUpdateUserRoleSuccess]
  )

  // Update the selected domains list when the role is changed
  useEffect(() => {
    setDialogError(undefined)
    if (selectedUserRole === AccountUserRole.domain_admin) {
      resetSelectedDomains(user.managedDomains)
    } else if (selectedUserRole === AccountUserRole.help_desk) {
      resetSelectedDomains(user.helpdeskDomains)
    } else {
      resetSelectedDomains([])
    }
  }, [selectedUserRole, user, resetSelectedDomains])

  // domains list is changed
  useEffect(() => {
    setDialogError(undefined)
  }, [selectedDomains])

  // Update is finished
  useEffect(() => {
    if (isUpdateUserRoleSuccess) {
      dispatch(setSuccessSnackBar({ message: 'update_account_user_role_success', params: [user.userId] }))
      onClose()
    } else if (isUpdateUserRoleFailed) {
      setDialogError(EditUserDialogErrors.userUpdateError)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdateUserRoleSuccess, isUpdateUserRoleFailed, dispatch, user])

  // Check if user has U permission on at least one domain's domain_level_policies
  const isDomainAdminRestricted =
    !authDomainPolicies ||
    authDomainPolicies.length === 0 ||
    !authDomainPolicies.some(
      policy => policy.subject === AuthSubject.DOMAIN_LEVEL_POLICIES && policy.action?.includes('U')
    )

  const availableUserRoles = useMemo(
    () =>
      Object.values(AccountUserRole)
        .filter(value => value !== AccountUserRole.all)
        // Only filter out domain_admin when in domain-manage mode AND user lacks policy access
        .filter(value => !isDomainAdminRestricted || value !== AccountUserRole.domain_admin),
    [isDomainAdminRestricted]
  )

  // Clear selection if current role is not in available roles
  useEffect(() => {
    if (selectedUserRole && !(availableUserRoles as string[]).includes(selectedUserRole)) {
      setSelectedUserRole('')
    }
  }, [availableUserRoles, selectedUserRole])

  const onSelectUserRole = useCallback((selectedRole: AccountUserRole) => {
    setSelectedUserRole(selectedRole)
  }, [])

  const onSave = useCallback(() => {
    // Prevent saving if no role selected or role is restricted
    if (!selectedUserRole || (isDomainAdminRestricted && selectedUserRole === AccountUserRole.domain_admin)) {
      return
    }
    const selectedIds = getSelectedIds(domains?.map(domain => domain.domainId) || [])
    if (
      [AccountUserRole.domain_admin, AccountUserRole.help_desk].includes(selectedUserRole as AccountUserRole) &&
      !selectedIds.length
    ) {
      setDialogError(EditUserDialogErrors.missedDomainSelection)
    } else {
      dispatch(
        updateAccountUserRole({
          userId: user.userId,
          role: selectedUserRole as AccountUserRole,
          domains: selectedIds
        })
      )
    }
  }, [selectedUserRole, dispatch, user, getSelectedIds, domains, isDomainAdminRestricted])

  const onCancel = useCallback(() => {
    onClose()
  }, [onClose])

  return useMemo(
    () => [
      {
        selectedUserRole,
        selectedDomains,
        domains,
        isAllDomainsSelected,
        dialogError,
        updateInProgress: isUpdateUserRoleInProgress,
        availableUserRoles
      },
      {
        onSave,
        onCancel,
        onSelectUserRole,
        onSelectDomain,
        onSelectAllDomains,
        isDomainSelected
      }
    ],
    [
      isDomainSelected,
      onSelectAllDomains,
      selectedUserRole,
      selectedDomains,
      onSave,
      onCancel,
      onSelectUserRole,
      onSelectDomain,
      domains,
      isAllDomainsSelected,
      dialogError,
      isUpdateUserRoleInProgress,
      availableUserRoles
    ]
  )
}
